import { bsMath, uiAmountToLamports } from "@bridgesplit/utils";
import { getWhirlpoolFromClmm, LoopRouteType, BaseRouteStepExpanded, LoanCreationParams } from "@bridgesplit/abf-sdk";
import { sqrtPriceX64ToTickIndex } from "@orca-so/whirlpool-client-sdk";
import { isPrincipalTokenWind } from "@bridgesplit/abf-react";
import { BN } from "bn.js";

import { BaseWinder } from "./BaseWinder";
import { LoopWinder, LoopWinderCtx } from "./types";

export class OrcaWinder extends BaseWinder implements LoopWinder {
    public readonly name = "OrcaWinder";

    getCollateralInputAmountForQuote(ctx: LoopWinderCtx): number | undefined {
        return 1;
    }

    getRouteOverrideParams(ctx: LoopWinderCtx): Partial<BaseRouteStepExpanded> | undefined {
        const { loopExpanded, form } = ctx;

        if (loopExpanded?.loopVault.routeType === LoopRouteType.Orca && form?.inputToken) {
            if (isPrincipalTokenWind(loopExpanded.principalToken.mint, form.inputToken.mint)) {
                return {
                    positionParams: {
                        principalMint: loopExpanded.principalToken.mint,
                        positionPrincipalInput: uiAmountToLamports(
                            form.inputAmount,
                            loopExpanded.principalToken.decimals
                        )
                    }
                } as Partial<BaseRouteStepExpanded>;
            }
            const whirlpoolPosition = getWhirlpoolFromClmm(form.clmmPosition);
            if (whirlpoolPosition) {
                return {
                    positionParams: {
                        existingPosition: true,
                        principalMint: loopExpanded.principalToken.mint,
                        positionPrincipalInput: uiAmountToLamports(
                            bsMath.div(form.inputAmount, loopExpanded.principalOracle.usdPrice),
                            loopExpanded.principalToken.decimals
                        )
                    },
                    tickUpper: sqrtPriceX64ToTickIndex(new BN(whirlpoolPosition.position.upperSqrtPrice)),
                    tickLower: sqrtPriceX64ToTickIndex(new BN(whirlpoolPosition.position.lowerSqrtPrice))
                } as Partial<BaseRouteStepExpanded>;
            }
        }
        return undefined;
    }

    getLeveragedCollateralAmountToDeposit(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, form, externalQuote } = ctx;
        if (!loopExpanded || !externalQuote || !form?.inputAmount || !form?.multiplier) return undefined;
        return 1;
    }

    getCollateralInputAmount(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, inputAmount, clmmPosition, oraclePrices } = ctx;

        if (clmmPosition && loopExpanded) {
            const { principalToken } = loopExpanded;
            const principalOracle = oraclePrices?.get(principalToken.mint);
            const orcaPositionUsd = clmmPosition.totalPrice;

            const orcaPositionInPrincipal = bsMath.div(orcaPositionUsd, principalOracle?.usdPrice);
            return orcaPositionInPrincipal;
        }
        return inputAmount;
    }

    calculateMarketCollateralAmounts(ctx: LoopWinderCtx) {
        const { orcaMinOutputAmount } = ctx;
        const { totalOrcaMinOutputAmount, swappedOrcaMinOutputAmount } = orcaMinOutputAmount ?? {
            totalOrcaMinOutputAmount: 0,
            swappedOrcaMinOutputAmount: 0
        };

        return {
            totalMinOutputCollateralAmount: totalOrcaMinOutputAmount,
            swappedMinOutputCollateralAmount: swappedOrcaMinOutputAmount
        };
    }

    getLoanCreationParams(ctx: LoopWinderCtx): LoanCreationParams {
        const { loopExpanded, form, whirlpoolData } = ctx;

        if (loopExpanded?.loopVault.routeType === LoopRouteType.Orca && whirlpoolData?.whirlpoolOffchain) {
            const routeParams = loopExpanded.loopVault.quoteFetchParams as any;
            const offchain = whirlpoolData.whirlpoolOffchain;

            if (routeParams.type === "orca" && offchain?.tickSpacing && offchain?.tokenMintA && offchain?.tokenMintB) {
                if (!form?.clmmPosition) {
                    return {
                        OrcaPositionCreate: {
                            upperTick: routeParams.tickUpper,
                            lowerTick: routeParams.tickLower,
                            tickSpacing: offchain.tickSpacing,
                            whirlpool: whirlpoolData.whirlpoolAddress || "",
                            tokenMintA: offchain.tokenMintA,
                            tokenMintB: offchain.tokenMintB
                        }
                    };
                }
            }
        }
        return "Default";
    }

    getPriceOverride(ctx: LoopWinderCtx): number | undefined {
        const { loopExpanded, inputAmount, orcaPosition } = ctx;
        if (orcaPosition) {
            return orcaPosition.totalPrice;
        } else {
            return loopExpanded?.principalOracle.getUsdAmount(inputAmount);
        }
    }
}
